/**
 * Page back-office : modération des commentaires d'actualités postés par les parents.
 */
import { useState } from 'react';
import { Trash2, RotateCcw, Search, X, ShieldAlert } from 'lucide-react';
import {
  useGetCommentairesParentsQuery,
  useModererCommentaireParentMutation,
  CommentaireParentItem,
} from '../../services/messagesParentsApi';

const RAISONS_PROPOSEES = [
  'Propos injurieux ou inappropriés',
  'Information personnelle exposée',
  'Hors-sujet par rapport à l\'actualité',
  'Doublon / spam',
  'Demandé par le parent',
];

export default function ModerationCommentaires() {
  const [supprime, setSupprime] = useState<boolean | undefined>(false);
  const [filtreActu, setFiltreActu] = useState<string>('');
  const [modalCible, setModalCible] = useState<CommentaireParentItem | null>(null);
  const [raison, setRaison] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, isFetching, refetch } = useGetCommentairesParentsQuery({
    supprime,
    actualite_id: filtreActu ? Number(filtreActu) : undefined,
  });
  const [moderer, { isLoading: actioning }] = useModererCommentaireParentMutation();

  const ouvrirModalSuppression = (c: CommentaireParentItem) => {
    setModalCible(c);
    setRaison('');
  };

  const handleRestaurer = async (id: number) => {
    try {
      await moderer({ id, action: 'restaurer' }).unwrap();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur.');
    }
  };

  const handleConfirmerSuppression = async () => {
    if (!modalCible) return;
    const r = raison.trim();
    if (r.length < 5) {
      alert('Raison de modération obligatoire (5 caractères minimum).');
      return;
    }
    setSubmitting(true);
    try {
      await moderer({ id: modalCible.id, action: 'supprimer', raison: r }).unwrap();
      setModalCible(null);
      setRaison('');
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Modération des commentaires</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Commentaires des parents sur les actualités du portail. Toute suppression est tracée (raison + modérateur).
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-1">
          {([
            { v: false, label: 'Actifs' },
            { v: true, label: 'Masqués' },
            { v: undefined, label: 'Tous' },
          ] as const).map((opt) => (
            <button key={String(opt.v)} onClick={() => setSupprime(opt.v)}
              className={`px-3 py-1 text-xs rounded ${
                supprime === opt.v
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="number" placeholder="ID actualité"
            value={filtreActu} onChange={(e) => setFiltreActu(e.target.value)}
            className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-32"
          />
        </div>

        <button onClick={() => refetch()} className="ml-auto px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">
          {isFetching ? '…' : 'Actualiser'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {!data || data.results.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun commentaire.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Parent</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Actu</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Commentaire</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {data.results.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 align-top">
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {new Date(c.date_creation).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{c.parent_prenoms} {c.parent_nom}</div>
                    <div className="text-[10px] text-gray-500">{c.parent_email || c.parent_telephone || ''}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-blue-500">#{c.actualite_id}</td>
                  <td className="px-3 py-3 text-xs text-gray-700 dark:text-gray-300 max-w-md">
                    <div>{c.contenu}</div>
                    {c.supprime && c.raison_suppression && (
                      <div className="mt-1.5 text-[10px] text-red-600 dark:text-red-300 italic border-l-2 border-red-300 dark:border-red-700 pl-2">
                        Masqué par {c.supprime_par_nom || '—'}
                        {c.date_suppression ? ' le ' + new Date(c.date_suppression).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        {' — '}{c.raison_suppression}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {c.supprime ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded">Masqué</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded">Actif</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {c.supprime ? (
                      <button onClick={() => handleRestaurer(c.id)} disabled={actioning}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded inline-flex items-center gap-1">
                        <RotateCcw size={11} /> Restaurer
                      </button>
                    ) : (
                      <button onClick={() => ouvrirModalSuppression(c)} disabled={actioning}
                        className="px-2 py-1 text-xs bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded inline-flex items-center gap-1">
                        <Trash2 size={11} /> Masquer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalCible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
             onClick={() => !submitting && setModalCible(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full border border-gray-200 dark:border-gray-700"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="text-red-600 dark:text-red-300" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Masquer le commentaire</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Indiquez la raison — elle sera conservée pour audit.
                  </p>
                </div>
              </div>
              <button onClick={() => !submitting && setModalCible(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={16} />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-3 p-2.5 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded text-xs text-gray-700 dark:text-gray-300">
                <div className="text-[10px] text-gray-500 mb-1">
                  {modalCible.parent_prenoms} {modalCible.parent_nom} — actu #{modalCible.actualite_id}
                </div>
                {modalCible.contenu}
              </div>

              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Raison de la modération <span className="text-red-500">*</span>
              </label>
              <textarea value={raison} onChange={(e) => setRaison(e.target.value)}
                rows={3} maxLength={500} disabled={submitting}
                placeholder="Ex. : propos inappropriés à l'égard d'un autre parent"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500" />

              <div className="flex flex-wrap gap-1 mt-2">
                {RAISONS_PROPOSEES.map(r => (
                  <button key={r} type="button" onClick={() => setRaison(r)} disabled={submitting}
                    className="px-2 py-1 text-[10px] bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600
                               text-gray-700 dark:text-gray-300 rounded">
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setModalCible(null)} disabled={submitting}
                className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                Annuler
              </button>
              <button onClick={handleConfirmerSuppression} disabled={submitting || raison.trim().length < 5}
                className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white rounded inline-flex items-center gap-1">
                <Trash2 size={11} /> {submitting ? 'Masquage…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
